import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './ActivityForm.css';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

// Social activities the venue hosts at fixed times, for ONE sport at a time: App.js
// mounts this under the "<Sport> Activities" button and passes the sports.name as
// `sport` (Padel or Pickle today — the data layer is sport-generic, and
// get_open_activities filters on that name). Users buy SEAT(s); each activity
// shows a live [booked/capacity] counter. The DATE is chosen via the shared
// Calendar in the left column (App.js "2. Date", same as court/coach) and passed
// in as `externalSelectedDate`; this component renders only the filtered list
// ("3. Activity") in the right column.
const DEFAULT_SPORT = 'Padel'; // what the pre-2026-09-08 (Padel-only) mount point meant
const UNLIMITED_STEP_CAP = 10; // sane max seats per booking for unlimited activities

// Date → "YYYY-MM-DD" (local, no timezone drift)
const dateKeyOf = (d) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null;

const formatActivityDate = (dateStr) => {
  const [y, m, d] = (dateStr || '').split('-').map(Number);
  if (!y) return dateStr;
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString('en-MY', { weekday: 'short', day: 'numeric', month: 'short' });
};

function ActivityForm({ sport = DEFAULT_SPORT, externalSelectedDate = null }) {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [seats, setSeats] = useState(1);
  // Lowercased profile gender ('male'/'female'/'' when unset); null while loading.
  const [myGender, setMyGender] = useState(null);
  // Monotonic id of the latest list fetch. Switching Padel → Pickle fires a new
  // RPC while the old one may still be in flight; only the newest response may
  // land, or a slow Padel reply could overwrite the Pickle list.
  const fetchSeq = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setMyGender(''); return; }
        const { data } = await supabase.from('profiles').select('gender').eq('user_id', user.id).single();
        setMyGender(String(data?.gender || '').toLowerCase());
      } catch {
        setMyGender('');
      }
    })();
  }, []);

  const fetchActivities = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      const { data, error: rpcError } = await supabase.rpc('get_open_activities', { p_sport_name: sport });
      if (seq !== fetchSeq.current) return; // superseded (sport switch / realtime refresh)
      if (rpcError) throw rpcError;
      setActivities(data || []);
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      console.error('ActivityForm: failed to fetch activities:', err);
      setError('Failed to load activities. Please try again.');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [sport]);

  // A sport change is a fresh page, not a refresh: blank the list, selection and
  // any stale error BEFORE the new fetch resolves, so the previous sport's rows
  // (and the "No activities on <date>" copy computed from them) never show under
  // the new heading. Realtime refreshes go through fetchActivities alone and keep
  // the list on screen, which is why this reset is keyed on `sport` only.
  useEffect(() => {
    setActivities([]);
    setError(null);
    setLoading(true);
    setSelectedId(null);
  }, [sport]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Live counter: any change to the activities table (booked_count / status) refreshes
  // the list, so [3/8] updates as other people join. Same channel pattern as Navbar credits.
  useEffect(() => {
    const channel = supabase
      .channel('activities-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'activities' }, () => {
        fetchActivities();
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') console.error('ActivityForm: realtime channel error');
      });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchActivities]);

  const dateKey = dateKeyOf(externalSelectedDate);
  const visible = useMemo(
    () => (dateKey ? activities.filter((a) => a.activity_date === dateKey) : []),
    [activities, dateKey]
  );

  // Collapse any open card when the selected date changes.
  useEffect(() => {
    setSelectedId(null);
  }, [dateKey]);

  const seatsRemaining = (a) => (a.capacity == null ? null : Math.max(a.capacity - a.booked_count, 0));
  const isFull = (a) => a.capacity != null && a.booked_count >= a.capacity;
  // Gender-restricted sessions (e.g. Ladies Rally): blocked when the profile
  // gender is loaded and doesn't match (unset gender blocks too — the server
  // enforces the same rule, this just explains it up front).
  const genderBlocked = (a) => !!a.gender_restriction && myGender !== null && myGender !== a.gender_restriction;
  const restrictionLabel = (a) => (a.gender_restriction === 'female' ? 'Ladies only' : 'Men only');
  // Partner-required minimum (>=1). A booking must buy at least this many seats.
  const minSeats = (a) => Math.max(1, Number(a.min_seats_per_booking) || 1);
  const stepMin = (a) => minSeats(a);
  const stepMax = (a) => {
    const rem = seatsRemaining(a);
    return rem == null ? UNLIMITED_STEP_CAP : Math.min(rem, UNLIMITED_STEP_CAP);
  };
  // Not enough seats remain to satisfy the per-booking minimum (e.g. min 2 but
  // only 1 left) — the session can't take a new booking even though it isn't "full".
  const tooFewLeft = (a) => stepMax(a) < stepMin(a);

  // A realtime refresh can shrink the remaining capacity under the user's chosen
  // seat count — re-clamp it (respecting the per-booking minimum), and drop the
  // selection entirely if the activity filled up / vanished while they decided.
  useEffect(() => {
    if (!selectedId) return;
    const a = activities.find((x) => x.id === selectedId);
    if (!a || isFull(a)) {
      setSelectedId(null);
      return;
    }
    const lo = stepMin(a);
    const hi = stepMax(a);
    setSeats((s) => Math.min(Math.max(lo, s), Math.max(lo, hi)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities, selectedId]);

  const handleSelect = (a) => {
    if (isFull(a)) return;
    const wasSelected = selectedId === a.id;
    setSelectedId(wasSelected ? null : a.id);
    if (!wasSelected) setSeats(stepMin(a)); // start at the minimum (partner-required)
  };

  const handleCheckout = (a) => {
    navigate('/checkout-activity', {
      state: {
        activity: {
          id: a.id,
          name: a.name,
          // ActivityCheckout stamps this into booking_metadata.sport_name (the v12
          // promo sport gate reads it) and brands the Navbar from it. The RPC
          // returns the row's own sports.name; the prop covers an older RPC shape.
          sport_name: a.sport_name ?? sport,
          description: a.description,
          level: a.level,
          activity_date: a.activity_date,
          start_time: a.start_time,
          end_time: a.end_time,
          price: Number(a.price),
          capacity: a.capacity,
          courts: a.courts,
        },
        seats,
      },
    });
  };

  return (
    <div className="activity-form">
      {loading && <div className="activity-loading">Loading activities…</div>}
      {error && <div className="activity-error">{error}</div>}
      {!loading && !error && activities.length === 0 && (
        <div className="activity-empty">No upcoming activities right now. Check back soon!</div>
      )}

      {!loading && !error && activities.length > 0 && !externalSelectedDate && (
        <div className="activity-empty">Pick a date on the calendar to see the activities on that day.</div>
      )}

      {!loading && !error && activities.length > 0 && externalSelectedDate && (
        <>
          <h2 className="step-title">3. Activity</h2>
          {visible.length === 0 ? (
            <div className="activity-empty">No activities on {formatActivityDate(dateKey)}. Try another day.</div>
          ) : (
            <div className="activity-list">
              {visible.map((a) => {
                const full = isFull(a);
                const rem = seatsRemaining(a);
                const selected = selectedId === a.id;
                const max = stepMax(a);
                const min = stepMin(a);
                const fewLeft = tooFewLeft(a);
                const partnerLabel = min === 2 ? 'Partner required' : `Groups of ${min}`;
                return (
                  <div key={a.id} className={`activity-card ${full ? 'is-full' : ''} ${selected ? 'is-selected' : ''}`}>
                    <div className="activity-card-main" onClick={() => handleSelect(a)}>
                      <div className="activity-card-header">
                        <span className="activity-name">{a.name}</span>
                        <span className={`activity-counter ${full ? 'full' : ''}`}>
                          {a.capacity == null
                            ? `${a.booked_count} joined · Unlimited`
                            : `${a.booked_count}/${a.capacity} players`}
                        </span>
                      </div>
                      {(a.gender_restriction || min > 1) && (
                        <div className="activity-badges">
                          {a.gender_restriction && (
                            <span className={`activity-level ${a.gender_restriction === 'female' ? 'ladies' : 'mens'}`}>
                              {restrictionLabel(a)}
                            </span>
                          )}
                          {min > 1 && (
                            <span className="activity-level partner">{partnerLabel}</span>
                          )}
                        </div>
                      )}
                      <ul className="activity-points">
                        <li>{a.start_time} – {a.end_time}</li>
                        <li>Level: {a.level || 'Open to all'}</li>
                        <li>RM {Number(a.price).toFixed(2)} per person</li>
                        {Array.isArray(a.courts) && a.courts.length > 0 && (
                          <li>{a.courts.length > 1 ? 'Courts' : 'Court'}: {a.courts.join(', ')}</li>
                        )}
                      </ul>
                      {a.description && <p className="activity-desc">{a.description}</p>}
                    </div>

                    {full && (
                      <div className="activity-full-note">
                        Fully booked — please contact the admin to be added.
                      </div>
                    )}

                    {selected && !full && genderBlocked(a) && (
                      <div className="activity-full-note">
                        {restrictionLabel(a) === 'Ladies only'
                          ? 'This session is for ladies only.'
                          : 'This session is for men only.'}
                        {myGender === '' && ' (Set your gender in your profile if this is a mistake.)'}
                      </div>
                    )}

                    {selected && !full && !genderBlocked(a) && fewLeft && (
                      <div className="activity-full-note">
                        Only {rem} seat{rem === 1 ? '' : 's'} left, but this session needs at least {min} per
                        booking — please contact the admin.
                      </div>
                    )}

                    {selected && !full && !genderBlocked(a) && !fewLeft && (
                      <div className="activity-book-panel">
                        <div className="activity-seats">
                          <span className="activity-seats-label">
                            Seats
                            {min > 1 && <span className="activity-min-hint"> · min {min}</span>}
                          </span>
                          <div className="pax-selector">
                            <button
                              className="pax-button"
                              onClick={() => setSeats(Math.max(min, seats - 1))}
                              disabled={seats <= min}
                            >
                              -
                            </button>
                            <span className="pax-count">{seats}</span>
                            <button
                              className="pax-button"
                              onClick={() => setSeats(Math.min(max, seats + 1))}
                              disabled={seats >= max}
                            >
                              +
                            </button>
                          </div>
                          {rem != null && <span className="activity-remaining">{rem} left</span>}
                        </div>
                        {min > 1 && (
                          <p className="activity-partner-note">
                            {min === 2
                              ? 'You need a partner — book 2 seats (find your own partner before joining).'
                              : `This is a group session — book at least ${min} seats.`}
                          </p>
                        )}
                        <p className="price-display">
                          RM {Number(a.price).toFixed(2)} × {seats} = RM {(Number(a.price) * seats).toFixed(2)}
                        </p>
                        <button className="activity-checkout-btn" onClick={() => handleCheckout(a)}>
                          Proceed to checkout
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ActivityForm;
